import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "configuration-management",
  kind: "horizontal",
  title: "Configuration Management Systems",
  shortTitle: "Configuration Management",
  status: "draft",
  tier: "experience",
  seoTitle: "Configuration Management Systems | General Applications",
  seoDescription:
    "Authoritative hardware, software, effectivity, revision, compatibility, and change-control systems for operational environments.",
  summary:
    "Configuration systems that keep equipment, software, revisions, applicability, and approved change connected to the work people actually perform.",
  image: "/images/blog/reconciling-fragmented-data-hero.webp",
  relatedCapabilitySlugs: ["data-search-knowledge"],
  relatedIndustrySlugs: ["military", "energy", "enterprise"],
  relatedPostSlugs: ["s1000d-issue-archaeology"],
  engineering: [
    "Authoritative hardware, software, network, and document configuration records",
    "Baselines, revision, effectivity, applicability, and compatibility models",
    "Asset, part, software, and technical-data relationship modeling",
    "Configuration change requests, approvals, implementation, and verification workflows",
    "Multi-source inventory reconciliation and configuration discrepancy analysis",
    "Traceability from approved baseline through fielded state and supporting evidence",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Configuration Is the State You Can Defend",
    },
    {
      type: "paragraph",
      text: "A configuration record is useful only when it connects the physical or software unit to its approved state: the applicable revision, parts, software, settings, supporting documents, and change history. Separate spreadsheets, asset lists, deployment records, and technical documents inevitably drift unless the relationships between them are explicit.",
    },
    {
      type: "heading",
      level: 2,
      text: "A Baseline Needs a Change Path",
    },
    {
      type: "paragraph",
      text: "The goal is not to freeze a system forever. It is to make authorized change visible from request through approval, implementation, verification, and the new baseline. That gives operators and maintainers a way to distinguish an intentional difference from an unknown one when work, support, audit, or incident response depends on the answer.",
    },
  ],
  phases: {
    study: {
      focus:
        "Identify the units, configuration sources, governing revisions, operational decisions, and change paths that must agree before defining a single source of truth.",
      produces: [
        "Configuration item, baseline, and relationship inventory",
        "Source-system and discrepancy analysis",
        "Effectivity, applicability, revision, and compatibility model",
        "Change authority, approval, and verification map",
      ],
      fit: {
        good: [
          "Operational environments where equipment, software, technical data, and approved changes must remain aligned",
          "Teams reconciling conflicting inventory, baseline, revision, or applicability records before a deployment or modernization effort",
        ],
        poor: [
          "A simple asset list with no baseline, change, or operational-decision requirement",
          "A request to declare a configuration authoritative without access to the systems and owners that establish it",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the configuration items, relationships, baseline rules, and change lifecycle that connect technical facts to operational decisions.",
      produces: [
        "Canonical configuration and relationship model",
        "Baseline, revision, effectivity, and compatibility architecture",
        "Change request, approval, implementation, and verification workflow",
        "Source-of-truth and synchronization boundary design",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the records, validation rules, reconciliation procedures, interfaces, and exception paths that make the model usable day to day.",
      produces: [
        "Configuration record and evidence specifications",
        "Baseline comparison, discrepancy, and reconciliation rules",
        "Change-control, implementation, and verification procedures",
        "Reporting and traceability definitions for operators and reviewers",
      ],
    },
    implementation: {
      focus:
        "Build the configuration system around the real sources and users, then verify that a change can be tracked through to the fielded state.",
      produces: [
        "Authoritative configuration records and relationship views",
        "Controlled change and baseline-update workflow",
        "Reconciled discrepancy and exception records",
        "Operational guidance for configuration ownership and maintenance",
      ],
    },
  },
};
